/***************************************************************************
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { isEmpty, isString } from 'lodash-es';
import { AsyncLock } from '@babylonjs/core/Misc/asyncLock';
import { A3D_TAGS, A3D_TAG_NAMES } from '../core';
import { TransformElement } from '../transform';
export var ModelInputAnimationStatus;
(function (ModelInputAnimationStatus) {
    ModelInputAnimationStatus["PLAY"] = "play";
    ModelInputAnimationStatus["PAUSE"] = "pause";
})(ModelInputAnimationStatus || (ModelInputAnimationStatus = {}));
export var ModelLoader;
(function (ModelLoader) {
    ModelLoader["GLB"] = "glb";
    ModelLoader["PLY"] = "ply";
    ModelLoader["OBJ"] = "obj";
    ModelLoader["STL"] = "stl";
})(ModelLoader || (ModelLoader = {}));
var A3dModelErrorNames;
(function (A3dModelErrorNames) {
    A3dModelErrorNames["PARENT_WARNING"] = "A3dModelParentWarning";
    A3dModelErrorNames["NO_MODEL_PROVIDED"] = "A3dModelNoMeshProvided";
})(A3dModelErrorNames || (A3dModelErrorNames = {}));
const A3dModelErrorMessages = {
    [A3dModelErrorNames.PARENT_WARNING]: `Transform element should be a child of ${A3D_TAGS.VIEWER} or ${A3D_TAGS.NODE}`,
    [A3dModelErrorNames.NO_MODEL_PROVIDED]: 'Attribute src is not set',
};
let A3dModel = class A3dModel extends TransformElement {
    constructor() {
        super(...arguments);
        /**
         * Source (url) of the model.
         * @attr
         */
        this.src = '';
        this._loadMultiModelLock = new AsyncLock();
        /**
         * The Babylon.JS Model object created from the loaded file.
         */
        this.model = null;
    }
    /**
     * Get the current animation group.
     * If no animation is selected, returns undefined.
     * @returns {AnimationGroup} - The current animation group
     */
    get currentAnimation() {
        return this.model?.assetContainer.animationGroups[this.model.selectedAnimation];
    }
    async _updateSrc() {
        if (this._viewer && this.src) {
            this.model = await this._viewer.addModel(this.src, this.loader && { pluginExtension: `.${this.loader}` });
            // Update the parent of meshes of the model
            const rootNode = this.model.assetContainer.meshes.find((mesh) => mesh.id === '__root__');
            for (const mesh of this.model.assetContainer.meshes) {
                if (mesh.material && 'ambientTextureStrength' in mesh.material) {
                    mesh.material.ambientTextureStrength =
                        this._viewer.getRendererOption('aoStrength');
                }
            }
            if (rootNode) {
                rootNode.parent = this.node3d;
            }
        }
    }
    _updateAnimationStatus() {
        if (this.animationStatus === ModelInputAnimationStatus.PLAY) {
            this.currentAnimation?.play(true);
        }
        else {
            this.currentAnimation?.pause();
        }
    }
    _updateSelectedAnimation() {
        if (this.model) {
            // We are forced to test undefined here because selectedAnimation can be 0
            this.model.selectedAnimation =
                this.selectedAnimation != undefined ? this.selectedAnimation : -1;
        }
    }
    async _initElement() {
        super._initElement();
        this._validateParent();
        await this._loadMultiModelLock.lockAsync(async () => {
            await this._updateSrc();
            if (this._viewer?.cameraReframe ||
                this._viewer?.loadedModels.length === 1) {
                this._viewer?.frameCamera();
            }
        });
        this._updateSelectedAnimation();
        this._updateAnimationStatus();
        this._viewer?.onRendererOptionsChanged.add(() => {
            if (this.model) {
                for (const mesh of this.model.assetContainer.meshes) {
                    if (mesh.material && 'ambientTextureStrength' in mesh.material) {
                        mesh.material.ambientTextureStrength =
                            this._viewer?.getRendererOption('aoStrength');
                    }
                }
            }
        });
    }
    async update(changedProperties) {
        super.update(changedProperties);
        this._validateProperties();
        if (changedProperties.has('src')) {
            await this._loadMultiModelLock.lockAsync(async () => {
                if (this.model && this._viewer) {
                    await this._viewer?.removeModel(this.model);
                    await this._updateSrc();
                    if (this._viewer.cameraReframe ||
                        this._viewer.loadedModels.length === 1) {
                        this._viewer?.frameCamera();
                    }
                }
            });
        }
        if (changedProperties.has('selectedAnimation')) {
            this._updateSelectedAnimation();
        }
        if (changedProperties.has('animationStatus')) {
            this._updateAnimationStatus();
        }
    }
    async disconnectedCallback() {
        await this._loadMultiModelLock.lockAsync(async () => {
            if (this.model && this._viewer) {
                this.node3d?.dispose();
                await this._viewer?.removeModel(this.model);
                if (this._viewer.cameraReframe) {
                    this._viewer.frameCamera();
                }
            }
        });
        super.disconnectedCallback();
    }
    /**
     * @internal
     */
    exposedData() {
        return {
            src: this.src,
            loader: this.loader,
            selectedAnimation: this.selectedAnimation,
            animationStatus: this.animationStatus,
        };
    }
    /**
     * @internal
     */
    _validateParent() {
        if (this.parentElement?.tagName === A3D_TAG_NAMES.VIEWER ||
            this.parentElement?.tagName === A3D_TAG_NAMES.NODE) {
            return true;
        }
        this._warn(A3dModelErrorMessages[A3dModelErrorNames.PARENT_WARNING]);
        return false;
    }
    /**
     * @internal
     */
    _validateProperties() {
        if (isString(this.src) && !isEmpty(this.src)) {
            return true;
        }
        else {
            this._warn(A3dModelErrorMessages[A3dModelErrorNames.NO_MODEL_PROVIDED]);
            return false;
        }
    }
    render() {
        return html `<slot></slot>`;
    }
};
__decorate([
    property({ type: String })
], A3dModel.prototype, "src", void 0);
__decorate([
    property({ type: String })
], A3dModel.prototype, "loader", void 0);
__decorate([
    property({ type: Number, attribute: 'selected-animation' })
], A3dModel.prototype, "selectedAnimation", void 0);
__decorate([
    property({ type: String, attribute: 'animation-status' })
], A3dModel.prototype, "animationStatus", void 0);
A3dModel = __decorate([
    customElement('a3d-model')
], A3dModel);
export { A3dModel, A3dModelErrorMessages, A3dModelErrorNames };
